/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import { RefObject, ReactNode } from "react";

import {
  Displayer,
  RequiredAsterisk,
  Select,
} from "@smart/itops-sb-design-system-dom";

import { SectionErrors, sectionFallback, SectionItem } from "../../types";
import { getUITranslation } from "../../utils/ui-translations";
import { ErrorSummary } from "../shared-components/error-summary";

export type SectionHeadingProps = {
  section: SectionItem;
  errorsRef?: RefObject<HTMLDivElement | null>;
  errors?: SectionErrors;
  hideErrors?: boolean;
  selectedLanguage?: string;
  setSelectedLanguage?: (language: string) => void;
  languageOptions?: Array<{ label: string; value: string }>;
  showLanguageSelector?: boolean;
  showVoiceInSectionHeading?: boolean;
  voiceConversation?: ReactNode;
  isMobile?: boolean;
};

export const SectionHeading = ({
  section: { title, description, hasRequired },
  errorsRef,
  errors,
  hideErrors,
  selectedLanguage,
  setSelectedLanguage,
  languageOptions,
  showLanguageSelector,
  showVoiceInSectionHeading,
  voiceConversation,
  isMobile,
}: SectionHeadingProps) => (
  <div className="flex justify-between items-start gap-medium flex-wrap-reverse">
    <div className="[&>h1]:text-h2 [&>h1]:font-semibold [&>h1]:break-words [&>h1]:font-poppins [&>p]:text-ui [&>p]:font-semibold">
      <Displayer paragraphTag="h1" value={title} fallback={sectionFallback} />
      <Displayer value={description} />
      {hasRequired && (
        <p>
          <small className="text-caption text-neutral-600 font-normal">
            <RequiredAsterisk className="mx-0" />{" "}
            {getUITranslation("indicatesRequiredInformation", selectedLanguage)}
          </small>
        </p>
      )}
      {errors && errors.total > 1 && !hideErrors && (
        <ErrorSummary
          errors={errors.errors}
          errorsRef={errorsRef}
          selectedLanguage={selectedLanguage}
        />
      )}
    </div>
    {((showLanguageSelector && languageOptions && setSelectedLanguage) ||
      showVoiceInSectionHeading) && (
      <div className="flex gap-medium items-center">
        {showLanguageSelector && languageOptions && setSelectedLanguage && (
          <div className="min-w-[14rem]">
            <Select
              id="select-languages-in-section-heading"
              placeholder="Select language"
              options={languageOptions}
              value={selectedLanguage}
              onChange={setSelectedLanguage}
              compact={isMobile}
            />
          </div>
        )}
        {showVoiceInSectionHeading && voiceConversation}
      </div>
    )}
  </div>
);
